import { useTranslation } from 'react-i18next'
import { useSlotT } from '@/cms/useSlotT'
import { Container, Reveal, PageHeader } from '@/components/ui'
import { unifiedMagazines } from '@/content/magazinesUnified'
import MagazineArticleCard from '@/components/cards/MagazineArticleCard'
import SEO from '@/utils/seo'
import { buildBreadcrumbList } from '@/utils/structuredData'

/**
 * MagazinePage — press / media coverage archive.
 *
 * Renders every entry in the unified magazine list (shared with the homepage
 * feature slider via `unifiedMagazines()`) as a card. PDF entries embed the
 * in-page viewer; external entries open the publisher site in a new tab. The
 * unified list prefers the CMS source (`cmsMagazines`) and falls back to the
 * static `MAGAZINES` array when the generated module is empty (e.g. fresh
 * deploy before the first `npm run cms:sync`).
 */
export default function MagazinePage() {
  const slotT = useSlotT('magazine')
  const { t } = useTranslation()

  const magazines = unifiedMagazines()

  return (
    <>
      <SEO
        title={slotT('seo.magazineTitle')}
        description={slotT('seo.magazineDescription')}
        keywords={slotT('seo.magazineKeywords')}
        jsonLd={buildBreadcrumbList([
          { name: t('nav.home'), path: '/' },
          { name: t('nav.magazine'), path: '/magazine' },
        ])}
      />
      {/* No `description`: the hero's paragraph repeated the interview's own
          summary (issue, date, topic, the "reactive Q&A → inquiry-driven
          exploration" angle) that the first card below prints in full. The
          card's version is the one linked to the article, so the hero
          paragraph was a second copy of the same words with nowhere to go. */}
      <PageHeader
        title={slotT('magazine.hero.title')}
      />

      {/* Article list */}
      <section
        className="py-section-y px-section-x bg-surface-2"
        aria-labelledby="magazine-archive-title"
      >
        <Container size="xl">
          {/* The visible archive header (eyebrow + H2) was removed on
              2026-11-03 — it repeated the H1 word for word. The heading
              itself stays, screen-reader-only, so the card H3s keep a parent
              heading and the landmark keeps an accessible name: the same
              trick GalleryDetail / MagazineDetailPage use for theirs. */}
          <h2 id="magazine-archive-title" className="sr-only">
            {slotT('magazine.archive.title')}
          </h2>
          <div className="flex flex-col gap-gap-cards">
            {magazines.map((mag, idx) => (
              <Reveal key={mag.slug} delay={idx * 0.05}>
                <MagazineArticleCard mag={mag} />
              </Reveal>
            ))}
          </div>
        </Container>
      </section>
    </>
  )
}